import React, { useState, useEffect } from 'react';
import { Link } from "react-router-dom"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"
import axios from "axios"
import { localserver } from "../../../api/common"
import { useUser } from "../../AdminUsers/UserContext"
import { FaChevronRight, FaUsers, FaUserPlus, FaEnvelope, FaCheck, FaTimes, FaEdit } from "react-icons/fa"

const AdminTeam = () => {
  const { checkPermission } = useUser()
  const [isSearchbar, toggleSearchBar] = useState(false)
  const [activeStep, setActiveStep] = useState(1)
  const [errors, setErrors] = useState({})
  const [editMode, setEditMode] = useState(false)
  const [selectedMemberId, setSelectedMemberId] = useState(null)

  const nextStep = () => {
    if (validateStep(activeStep)) {
      if (activeStep < 3) setActiveStep(activeStep + 1)
    }
  }

  const prevStep = () => {
    if (activeStep > 1) setActiveStep(activeStep - 1)
  }

  const [teamMembers, setTeamMembers] = useState([])
  const [eventData, setEventData] = useState([])
  const [selectedEvents, setSelectedEvents] = useState([])

  const getEventByUser = async (event_status) => {
    try {
      const response = await axios.post(
        `${localserver}/get_events_by_user`,
        { event_status },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )
      if (response.data.success) {
        setEventData(response.data.events)
      } else {
        console.error("Error: Failed to fetch user data")
        showToast("Failed to fetch events", "error")
      }
    } catch (error) {
      console.error("Error fetching events:", error)
      showToast("An error occurred while fetching events", "error")
    }
  }

  const getTeamMembers = async () => {
    try {
      const response = await axios.get(`${localserver}/getTeamMembers`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      })
      if (response.data.success) {
        setTeamMembers(response.data.data)
      } else {
        console.error("Error: Failed to fetch user data")
        showToast("Failed to fetch events", "error")
      }
    } catch (error) {
      console.error("Error fetching events:", error)
      showToast("An error occurred while fetching events", "error")
    }
  }

  const [formState, setFormState] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: 1,
    allEvents: false,
  })

  const handleFormChange = (field, value) => {
    setFormState((prevState) => ({
      ...prevState,
      [field]: value,
    }))
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }))
    }
  }

  const validateStep = (step) => {
    const newErrors = {}
    switch (step) {
      case 1:
        if (!formState.first_name.trim()) {
          newErrors.first_name = "First Name is required"
        }
        if (!formState.email.trim()) {
          newErrors.email = "Email is required"
        } else if (!/\S+@\S+\.\S+/.test(formState.email)) {
          newErrors.email = "Email is invalid"
        }
        break
      case 2:
        if (!formState.role) {
          newErrors.role = "Role is required"
        }
        break
      case 3:
        if (!formState.allEvents && selectedEvents.length === 0) {
          newErrors.events = "Please select at least one event or choose All Events"
        }
        break
      default:
        break
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleEventToggle = (eventId, checked) => {
    setSelectedEvents((prev) => (checked ? [...prev, eventId] : prev.filter((id) => id !== eventId)))
    if (errors.events) {
      setErrors((prev) => ({ ...prev, events: "" }))
    }
  }

  const handleAllEventsChange = (checked) => {
    handleFormChange("allEvents", checked)
    if (checked) {
      setSelectedEvents([])
    }
    if (errors.events) {
      setErrors((prev) => ({ ...prev, events: "" }))
    }
  }

  const handleSubmit = async () => {
    if (validateStep(3)) {
      try {
        const postData = {
          email_id: formState.email,
          first_name: formState.first_name,
          last_name: formState.last_name || "",
          user_type: "admin",
          has_all_events_access: formState.allEvents ? "yes" : "no",
          event_access: formState.allEvents ? "" : selectedEvents.join(","),
          role_id: formState.role,
        }

        let response
        if (editMode) {
          postData.id = selectedMemberId
          response = await axios.post(`${localserver}/updateTeamMember`, postData, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          })
          if (response.data.success) {
            showToast("Team member updated successfully", "success")
            await getTeamMembers()
            setActiveStep(4)
          } else {
            showToast("Failed to update team member", "error")
          }
        } else {
          response = await axios.post(`${localserver}/create_team_member`, postData, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          })
          if (response.data.success) {
            showToast("Team member created successfully", "success")
            await getTeamMembers()
            setActiveStep(4)
          } else {
            showToast("Failed to create team member", "error")
          }
        }
      } catch (error) {
        console.error("Error creating/updating team member:", error)
        showToast("An error occurred while creating/updating team member", "error")
      }
    }
  }

  const handleAddMore = () => {
    setEditMode(false)
    setSelectedMemberId(null)
    setFormState({
      first_name: "",
      last_name: "",
      email: "",
      role: 1,
      allEvents: false,
    })
    setSelectedEvents([])
    setErrors({})
    setActiveStep(1)
  }

  const handleCreateTeamMates = async () => {
    const hasPermission = await checkPermission("Admin Team", "create")
    if (!hasPermission) return
    setEditMode(false)
    setSelectedMemberId(null)
    toggleSearchBar(true)
  }

  const handleClose = () => {
    setEditMode(false)
    setSelectedMemberId(null)
    toggleSearchBar(false)
    setActiveStep(1)
    setErrors({})
  }

  const openEditMode = async (member) => {
    const hasPermission = await checkPermission("Admin Team", "update")
    if (!hasPermission) return
    setEditMode(true)
    setSelectedMemberId(member.id)
    setFormState({
      first_name: member.first_name || "",
      last_name: member.last_name || "",
      email: member.email_id,
      role: Number(member.role_id),
      allEvents: member.has_all_events_access === "yes",
    })
    setSelectedEvents(
      member.has_all_events_access === "yes" ? [] : member.event_permissions.map((ep) => ep.event_id.toString()),
    )
    toggleSearchBar(true)
    setActiveStep(1)
  }

  const [checkUserLoading, setCheckUserLoading] = useState(false)
  const fetchPermission = async () => {
    setCheckUserLoading(true)
    try {
      const hasPermission = await checkPermission("Admin Team", "view")
      if (!hasPermission) return
    } catch (error) {
      console.error("Error Check Permission:", error)
    } finally {
      setCheckUserLoading(false)
    }
  }

  useEffect(() => {
    fetchPermission()
  }, [])

  useEffect(() => {
    getEventByUser("all")
    getTeamMembers()
  }, [])

  useEffect(() => {
    if (isSearchbar && !editMode) {
      setFormState({
        first_name: "",
        last_name: "",
        email: "",
        role: 1,
        allEvents: false,
      })
      setSelectedEvents([])
      setErrors({})
      setActiveStep(1)
    }
  }, [isSearchbar, editMode])

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    })
  }

  const getEventsText = (member) => {
    if (member.has_all_events_access === "yes") {
      return "All Events"
    } else {
      return `Limited Events (${member.event_permissions.length})`
    }
  }

  const getStatusClass = (status) => {
    return status === "active" ? "text-success" : "text-danger"
  }

  const getStatusText = (status) => {
    return status === "active" ? "Active" : "Deleted"
  }

  return (
    <>
      <ToastMessage />
      <div className={`overlay ${isSearchbar ? "active" : ""}`} onClick={() => toggleSearchBar(false)}></div>

      {checkUserLoading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "500px" }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      ) : (
        <div style={{ minHeight: "100vh", backgroundColor: "#f9fafb", padding: "24px" }}>
          {/* Header */}
          <div style={{ marginBottom: "22px" }}>
            <div
              className="brood_comes_div"
            >
              <div className="row align-items-center">
                <div className="col-md-6 brood_comes_heading">
                  <h2>
                    <div className="brood_comes_part">
                      <FaUsers size={20} />
                    </div>
                    Admin Team
                  </h2>
                  {/* <p style={{ margin: "8px 0 0 0", fontSize: "14px", color: "#666666" }}>
                    Manage your team members and permissions
                  </p> */}
                </div>

                <div className="col-md-6 text-end">
                  <button
                    onClick={handleCreateTeamMates}
                    style={{
                      background: "#184778",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      padding: "10px 20px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      transition: "all 0.3s ease",
                      boxShadow: "0 2px 4px rgba(24, 71, 120, 0.2)",
                      opacity: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#0f3a5f"
                      e.currentTarget.style.transform = "translateY(-2px)"
                      e.currentTarget.style.boxShadow = "0 4px 8px rgba(24, 71, 120, 0.3)"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "#184778"
                      e.currentTarget.style.transform = "translateY(0)"
                      e.currentTarget.style.boxShadow = "0 2px 4px rgba(24, 71, 120, 0.2)"
                    }}
                  >
                    <FaUserPlus size={16} />
                    Add Teammates
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div
            style={{
              backgroundColor: "#ffffff",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            }}
          >
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                    <th
                      style={{
                        padding: "16px 24px",
                        textAlign: "left",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Name / Status
                    </th>
                    <th
                      style={{
                        padding: "16px 24px",
                        textAlign: "left",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Date Joined
                    </th>
                    <th
                      style={{
                        padding: "16px 24px",
                        textAlign: "left",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Email
                    </th>
                    <th
                      style={{
                        padding: "16px 24px",
                        textAlign: "left",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Events
                    </th>
                    <th
                      style={{
                        padding: "16px 24px",
                        textAlign: "left",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Role
                    </th>
                    <th
                      style={{
                        padding: "16px 24px",
                        textAlign: "center",
                        fontSize: "13px",
                        fontWeight: "600",
                        color: "#6b7280",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {teamMembers.map((member, index) => (
                    <tr
                      key={member.id}
                      style={{
                        borderBottom: "1px solid #f3f4f6",
                        transition: "background-color 0.2s ease",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#f9fafb")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                    >
                      <td style={{ padding: "20px 24px" }}>
                        <div>
                          <a
                            onClick={() => openEditMode(member)}
                            style={{
                              color: "#184778",
                              fontSize: "14px",
                              fontWeight: "600",
                              textDecoration: "none",
                              cursor: "pointer",
                              transition: "color 0.2s ease",
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = "#2563a8")}
                            onMouseLeave={(e) => (e.currentTarget.style.color = "#184778")}
                          >
                            {member.first_name} {member.last_name}
                          </a>
                          <p
                            style={{
                              margin: "4px 0 0 0",
                              fontSize: "12px",
                              fontWeight: "500",
                              color: member.status === "active" ? "#10b981" : "#ef4444",
                            }}
                          >
                            {getStatusText(member.status)}
                          </p>
                        </div>
                      </td>
                      <td style={{ padding: "20px 24px", fontSize: "14px", color: "#6b7280" }}>
                        {formatDate(member.created_at)}
                      </td>
                      <td style={{ padding: "20px 24px", fontSize: "14px", color: "#6b7280" }}>{member.email_id}</td>
                      <td style={{ padding: "20px 24px" }}>
                        <span
                          style={{
                            padding: "4px 12px",
                            borderRadius: "12px",
                            fontSize: "12px",
                            fontWeight: "500",
                            backgroundColor: member.has_all_events_access === "yes" ? "#dbeafe" : "#fef3c7",
                            color: member.has_all_events_access === "yes" ? "#1e40af" : "#92400e",
                          }}
                        >
                          {getEventsText(member)}
                        </span>
                      </td>
                      <td style={{ padding: "20px 24px", fontSize: "14px", color: "#6b7280" }}>{member.role}</td>
                      <td style={{ padding: "20px 24px", textAlign: "center" }}>
                        <button
                          onClick={() => openEditMode(member)}
                          style={{
                            padding: "8px 16px",
                            backgroundColor: "#f3f4f6",
                            border: "1px solid #e5e7eb",
                            borderRadius: "6px",
                            color: "#374151",
                            fontSize: "13px",
                            fontWeight: "500",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = "#e5e7eb"
                            e.currentTarget.style.borderColor = "#d1d5db"
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "#f3f4f6"
                            e.currentTarget.style.borderColor = "#e5e7eb"
                          }}
                        >
                          <FaEdit size={12} />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                  {teamMembers.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        style={{
                          padding: "60px 24px",
                          textAlign: "center",
                          color: "#9ca3af",
                        }}
                      >
                        <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                          <FaUsers />
                        </div>
                        <h3
                          style={{
                            fontSize: "18px",
                            fontWeight: "600",
                            color: "#374151",
                            marginBottom: "8px",
                          }}
                        >
                          No team members yet
                        </h3>
                        <p style={{ fontSize: "14px", margin: 0 }}>Add your first team member to get started</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Sidebar Form */}
          <div className={`extraspace_search_div1 search_div${isSearchbar ? " open" : ""}`}>
            <div
              style={{
                backgroundColor: "#ffffff",
                height: "100%",
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Sticky Header */}
              <div
                style={{
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  background: "linear-gradient(135deg, #184778 0%, #2563a8 100%)",
                  padding: "20px 24px",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    {activeStep > 1 && (
                      <button
                        onClick={prevStep}
                        style={{
                          background: "rgba(255, 255, 255, 0.2)",
                          border: "none",
                          borderRadius: "8px",
                          color: "#ffffff",
                          padding: "8px 16px",
                          fontSize: "14px",
                          fontWeight: "500",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")}
                      >
                        ← Back
                      </button>
                    )}
                    <div>
                      <h3 style={{ margin: 0, color: "#ffffff", fontSize: "18px", fontWeight: "600" }}>
                        {editMode ? "Edit Teammate" : "Add Teammate"}
                      </h3>
                      <p style={{ margin: "4px 0 0 0", color: "rgba(255, 255, 255, 0.8)", fontSize: "13px" }}>
                        Step {activeStep} of 4
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    style={{
                      background: "rgba(255, 255, 255, 0.2)",
                      border: "none",
                      borderRadius: "8px",
                      color: "#ffffff",
                      width: "36px",
                      height: "36px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "18px",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.3)")}
                    onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255, 255, 255, 0.2)")}
                  >
                    <FaTimes />
                  </button>
                </div>

                {/* Progress Bar */}
                <div
                  style={{
                    marginTop: "16px",
                    height: "4px",
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderRadius: "2px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      height: "100%",
                      backgroundColor: "#ffffff",
                      borderRadius: "2px",
                      width: `${(activeStep / 4) * 100}%`,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
              </div>

              {/* Content Area */}
              <div style={{ flex: 1, padding: "32px 24px" }}>
                {/* Step 1: Basic Info */}
                {activeStep === 1 && (
                  <div>
                    <div
                      style={{
                        marginBottom: "24px",
                        padding: "20px",
                        backgroundColor: "#f0f9ff",
                        borderRadius: "12px",
                        border: "1px solid #bae6fd",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#184778",
                        }}
                      >
                        Basic Information
                      </h4>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#0369a1",
                          lineHeight: "1.5",
                        }}
                      >
                        Enter the team member's basic details to get started
                      </p>
                    </div>

                    <div style={{ display: "grid", gap: "20px" }}>
                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "8px",
                          }}
                        >
                          First Name <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="text"
                          value={formState.first_name}
                          onChange={(e) => handleFormChange("first_name", e.target.value)}
                          placeholder="Enter first name"
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: errors.first_name ? "2px solid #ef4444" : "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                            transition: "all 0.2s ease",
                          }}
                          onFocus={(e) => {
                            if (!errors.first_name) e.currentTarget.style.borderColor = "#184778"
                          }}
                          onBlur={(e) => {
                            if (!errors.first_name) e.currentTarget.style.borderColor = "#e5e7eb"
                          }}
                        />
                        {errors.first_name && (
                          <p
                            style={{
                              margin: "6px 0 0 0",
                              fontSize: "13px",
                              color: "#ef4444",
                            }}
                          >
                            {errors.first_name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "8px",
                          }}
                        >
                          Last Name
                        </label>
                        <input
                          type="text"
                          value={formState.last_name}
                          onChange={(e) => handleFormChange("last_name", e.target.value)}
                          placeholder="Enter last name"
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                            transition: "all 0.2s ease",
                          }}
                          onFocus={(e) => (e.currentTarget.style.borderColor = "#184778")}
                          onBlur={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
                        />
                      </div>

                      <div>
                        <label
                          style={{
                            display: "block",
                            fontSize: "14px",
                            fontWeight: "500",
                            color: "#374151",
                            marginBottom: "8px",
                          }}
                        >
                          Email <span style={{ color: "#ef4444" }}>*</span>
                        </label>
                        <input
                          type="email"
                          value={formState.email}
                          onChange={(e) => handleFormChange("email", e.target.value)}
                          placeholder="Enter email address"
                          style={{
                            width: "100%",
                            padding: "12px 16px",
                            border: errors.email ? "2px solid #ef4444" : "2px solid #e5e7eb",
                            borderRadius: "8px",
                            fontSize: "14px",
                            outline: "none",
                            transition: "all 0.2s ease",
                          }}
                          onFocus={(e) => {
                            if (!errors.email) e.currentTarget.style.borderColor = "#184778"
                          }}
                          onBlur={(e) => {
                            if (!errors.email) e.currentTarget.style.borderColor = "#e5e7eb"
                          }}
                        />
                        {errors.email && (
                          <p
                            style={{
                              margin: "6px 0 0 0",
                              fontSize: "13px",
                              color: "#ef4444",
                            }}
                          >
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Step 2: Role Selection */}
                {activeStep === 2 && (
                  <div>
                    <div
                      style={{
                        marginBottom: "24px",
                        padding: "20px",
                        backgroundColor: "#fef3c7",
                        borderRadius: "12px",
                        border: "1px solid #fde68a",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#92400e",
                        }}
                      >
                        Roles & Permissions
                      </h4>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#b45309",
                          lineHeight: "1.5",
                        }}
                      >
                        Assign a role to define access levels and permissions
                      </p>
                    </div>

                    {/* Email Badge */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        backgroundColor: "#f9fafb",
                        borderRadius: "8px",
                        marginBottom: "24px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FaEnvelope style={{ color: "#6b7280" }} />
                        <span style={{ fontSize: "14px", color: "#374151" }}>{formState.email}</span>
                      </div>
                    </div>

                    <div>
                      <label
                        style={{
                          display: "block",
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#374151",
                          marginBottom: "8px",
                        }}
                      >
                        Role <span style={{ color: "#ef4444" }}>*</span>
                      </label>
                      <select
                        value={formState.role}
                        onChange={(e) => handleFormChange("role", e.target.value)}
                        style={{
                          width: "100%",
                          padding: "12px 16px",
                          border: errors.role ? "2px solid #ef4444" : "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "14px",
                          outline: "none",
                          backgroundColor: "#ffffff",
                          transition: "all 0.2s ease",
                        }}
                        onFocus={(e) => {
                          if (!errors.role) e.currentTarget.style.borderColor = "#184778"
                        }}
                        onBlur={(e) => {
                          if (!errors.role) e.currentTarget.style.borderColor = "#e5e7eb"
                        }}
                      >
                        <option value="">Select Role</option>
                        <option value={2}>Admin</option>
                        <option value={3}>Manager</option>
                        <option value={4}>Support</option>
                      </select>
                      {errors.role && (
                        <p
                          style={{
                            margin: "6px 0 0 0",
                            fontSize: "13px",
                            color: "#ef4444",
                          }}
                        >
                          {errors.role}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Step 3: Event Access */}
                {activeStep === 3 && (
                  <div>
                    <div
                      style={{
                        marginBottom: "24px",
                        padding: "20px",
                        backgroundColor: "#f0fdf4",
                        borderRadius: "12px",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <h4
                        style={{
                          margin: "0 0 8px 0",
                          fontSize: "16px",
                          fontWeight: "600",
                          color: "#166534",
                        }}
                      >
                        Event Access
                      </h4>
                      <p
                        style={{
                          margin: 0,
                          fontSize: "14px",
                          color: "#15803d",
                          lineHeight: "1.5",
                        }}
                      >
                        Choose which events this team member can access
                      </p>
                    </div>

                    {/* Email Badge */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        padding: "12px 16px",
                        backgroundColor: "#f9fafb",
                        borderRadius: "8px",
                        marginBottom: "24px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <FaEnvelope style={{ color: "#6b7280" }} />
                        <span style={{ fontSize: "14px", color: "#374151" }}>{formState.email}</span>
                      </div>
                    </div>

                    {/* All Events Checkbox */}
                    <div
                      style={{
                        padding: "16px",
                        backgroundColor: "#f9fafb",
                        borderRadius: "8px",
                        marginBottom: "16px",
                      }}
                    >
                      <label
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          cursor: "pointer",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={formState.allEvents}
                          onChange={(e) => handleAllEventsChange(e.target.checked)}
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#374151",
                          }}
                        >
                          All Events
                        </span>
                      </label>
                    </div>

                    {/* Individual Events */}
                    {!formState.allEvents && (
                      <div
                        style={{
                          display: "grid",
                          gap: "8px",
                          maxHeight: "300px",
                          overflowY: "auto",
                          padding: "4px",
                        }}
                      >
                        {eventData.map((event) => (
                          <label
                            key={event.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              padding: "12px 16px",
                              backgroundColor: selectedEvents.includes(event.id.toString()) ? "#f0f9ff" : "#ffffff",
                              border: selectedEvents.includes(event.id.toString())
                                ? "2px solid #184778"
                                : "2px solid #e5e7eb",
                              borderRadius: "8px",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              if (!selectedEvents.includes(event.id.toString())) {
                                e.currentTarget.style.borderColor = "#d1d5db"
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!selectedEvents.includes(event.id.toString())) {
                                e.currentTarget.style.borderColor = "#e5e7eb"
                              }
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedEvents.includes(event.id.toString())}
                              onChange={(e) => handleEventToggle(event.id.toString(), e.target.checked)}
                              style={{
                                width: "18px",
                                height: "18px",
                                cursor: "pointer",
                              }}
                            />
                            <span
                              style={{
                                fontSize: "14px",
                                color: "#374151",
                                flex: 1,
                              }}
                            >
                              {event.event_name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}

                    {formState.allEvents && (
                      <p
                        style={{
                          margin: "16px 0 0 0",
                          fontSize: "14px",
                          color: "#6b7280",
                          fontStyle: "italic",
                        }}
                      >
                        All events access granted. Individual events disabled.
                      </p>
                    )}

                    {errors.events && (
                      <p
                        style={{
                          margin: "16px 0 0 0",
                          fontSize: "13px",
                          color: "#ef4444",
                        }}
                      >
                        {errors.events}
                      </p>
                    )}
                  </div>
                )}

                {/* Step 4: Success */}
                {activeStep === 4 && (
                  <div style={{ textAlign: "center" }}>
                    <div
                      style={{
                        width: "80px",
                        height: "80px",
                        backgroundColor: "#d1fae5",
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 24px auto",
                      }}
                    >
                      <FaCheck style={{ fontSize: "40px", color: "#10b981" }} />
                    </div>

                    <h3
                      style={{
                        fontSize: "24px",
                        fontWeight: "700",
                        color: "#1f2937",
                        marginBottom: "12px",
                      }}
                    >
                      {editMode ? "Teammate Updated!" : "Invitation Sent!"}
                    </h3>

                    <p
                      style={{
                        fontSize: "14px",
                        color: "#6b7280",
                        marginBottom: "32px",
                        lineHeight: "1.6",
                      }}
                    >
                      {editMode
                        ? "The teammate has been updated successfully. Would you like to add another teammate?"
                        : "The invitation has been sent successfully. Would you like to add more teammates?"}
                    </p>

                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        justifyContent: "center",
                        flexWrap: "wrap",
                      }}
                    >
                      <button
                        onClick={handleAddMore}
                        style={{
                          padding: "12px 32px",
                          background: "linear-gradient(135deg, #184778 0%, #2563a8 100%)",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                          boxShadow: "0 4px 12px rgba(24, 71, 120, 0.3)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)"
                          e.currentTarget.style.boxShadow = "0 6px 16px rgba(24, 71, 120, 0.4)"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)"
                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(24, 71, 120, 0.3)"
                        }}
                      >
                        Add More
                      </button>
                      <button
                        onClick={handleClose}
                        style={{
                          padding: "12px 32px",
                          backgroundColor: "#ffffff",
                          color: "#6b7280",
                          border: "2px solid #e5e7eb",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: "600",
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = "#f9fafb"
                          e.currentTarget.style.borderColor = "#d1d5db"
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = "#ffffff"
                          e.currentTarget.style.borderColor = "#e5e7eb"
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer Buttons (Steps 1-3 only) */}
              {activeStep < 4 && (
                <div
                  style={{
                    position: "sticky",
                    bottom: 0,
                    backgroundColor: "#ffffff",
                    padding: "20px 24px",
                    borderTop: "1px solid #e5e7eb",
                    boxShadow: "0 -4px 6px rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <button
                    onClick={activeStep === 3 ? handleSubmit : nextStep}
                    style={{
                      width: "100%",
                      padding: "14px 24px",
                      background: "linear-gradient(135deg, #184778 0%, #2563a8 100%)",
                      color: "#ffffff",
                      border: "none",
                      borderRadius: "8px",
                      fontSize: "15px",
                      fontWeight: "600",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: "0 4px 12px rgba(24, 71, 120, 0.3)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)"
                      e.currentTarget.style.boxShadow = "0 6px 16px rgba(24, 71, 120, 0.4)"
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)"
                      e.currentTarget.style.boxShadow = "0 4px 12px rgba(24, 71, 120, 0.3)"
                    }}
                  >
                    {activeStep === 3 ? "Complete" : "Continue"}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default AdminTeam
